import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useRef, useState, type ReactElement, type ReactNode } from 'react';

export type ConfirmDialogProps = {
  busy?: boolean;
  busyLabel: string;
  cancelLabel?: string;
  confirmLabel: string;
  description: ReactNode;
  errorMessage?: ReactNode;
  impact?: ReactNode;
  onConfirm: () => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  preserved?: ReactNode;
  title: string;
  trigger: ReactElement;
};

/**
 * Product-owned irreversible-action surface backed by Radix's alert-dialog primitive.
 * Visuals stay on HoedokLoop tokens; alert semantics, focus containment and trigger
 * restoration come from the shared primitive instead of route-local keyboard handling.
 */
export function ConfirmDialog({
  busy = false,
  busyLabel,
  cancelLabel = '취소',
  confirmLabel,
  description,
  errorMessage,
  impact,
  onConfirm,
  onOpenChange,
  open,
  preserved,
  title,
  trigger,
}: ConfirmDialogProps) {
  const cancelButton = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const submitLocked = useRef(false);
  const [locallySubmitting, setLocallySubmitting] = useState(false);
  const pending = busy || locallySubmitting;

  const requestOpenChange = (nextOpen: boolean) => {
    if (pending && !nextOpen) return;
    onOpenChange(nextOpen);
  };

  const requestConfirm = () => {
    if (submitLocked.current || pending) return;
    submitLocked.current = true;
    setLocallySubmitting(true);
    void (async () => {
      try {
        await onConfirm();
      } finally {
        submitLocked.current = false;
        setLocallySubmitting(false);
      }
    })();
  };

  return (
    <AlertDialog.Root onOpenChange={requestOpenChange} open={open}>
      <AlertDialog.Trigger asChild>{trigger}</AlertDialog.Trigger>
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className="pn-dialog-overlay"
          data-busy={pending || undefined}
          onPointerDown={(event) => event.preventDefault()}
        />
        <AlertDialog.Content
          aria-busy={pending || undefined}
          aria-modal="true"
          className="pn-dialog-content"
          data-busy={pending || undefined}
          onEscapeKeyDown={(event) => {
            if (pending) event.preventDefault();
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            content.current?.focus({ preventScroll: true });
          }}
          ref={content}
          tabIndex={-1}
        >
          <div className="pn-dialog-heading">
            <AlertDialog.Title>{title}</AlertDialog.Title>
            <AlertDialog.Description>{description}</AlertDialog.Description>
          </div>
          {impact ? <div className="pn-dialog-impact">{impact}</div> : null}
          {preserved ? <div className="pn-dialog-preserved">{preserved}</div> : null}
          {errorMessage ? (
            <div className="pn-dialog-error" role="alert">
              {errorMessage}
            </div>
          ) : null}
          <div className="pn-dialog-actions">
            <AlertDialog.Cancel asChild>
              <button className="pn-button" disabled={pending} ref={cancelButton} type="button">
                {cancelLabel}
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                className="pn-button"
                data-tone="danger"
                disabled={pending}
                onClick={(event) => {
                  event.preventDefault();
                  requestConfirm();
                }}
                type="button"
              >
                <span aria-live="polite">{pending ? busyLabel : confirmLabel}</span>
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
